import { pricesURL } from '@lessonnine/babbel-urls.js';

import { DASHBOARD_URL, RUNTIME_ENVIRONMENT } from 'src/dataLayer/env/index.js';
import { learnLanguages } from 'src/dataLayer/localData/learnLanguages';
import type { QueryParams } from './index';

// eslint-disable-next-line regexp/no-unused-capturing-group
const BABBEL_URL_REGEX = /([\da-z]+\.)?(babbel)\.(com|cn|io)/;

const isValidReturnToURLDomain = (returnToFromQuery: string): boolean => {
  const validDomain = RUNTIME_ENVIRONMENT === 'production' ? '.com' : '.cn';
  return new RegExp(validDomain).test(returnToFromQuery);
};

const isValidBabbelReturnUrl = (returnToFromQuery: string): boolean => {
  try {
    const decodedReturnToURLObject = new URL(decodeURIComponent(returnToFromQuery));
    return BABBEL_URL_REGEX.test(decodedReturnToURLObject.hostname);
  } catch {
    return false;
  }
};

/*
 * Returns the `return_to` query param of a URL if the value is a valid Babbel url.
 * If the return_to param is missing or is not a Babbel url, it returns the dashboard url.
 */
export const getReturnToUrl = (
  queryParams: QueryParams,
  fallbackUrl = DASHBOARD_URL
): string => {
  const { return_to: returnToURL } = Object.fromEntries(new URLSearchParams(queryParams));
  if (!returnToURL) return fallbackUrl;
  return isValidBabbelReturnUrl(returnToURL) && isValidReturnToURLDomain(returnToURL) ? returnToURL : fallbackUrl;
};

export const getUpdatedLangReturnToURL = (
  queryParams: QueryParams,
  learnLang: string
): string => {
  // we need to include learn_language_alpha3 in the regex to avoid changing the country_code3 in checkout urls.
  const LEARN_LANG_ALPHA3_REGEX = new RegExp(`learn_language_alpha3=(${Object.keys(learnLanguages).join('|')})`);
  const SUBDOMAIN_REGEX = '(de|uk|es|fr|it|pl|pt|se)';
  const PRICES_DOMAIN_REGEX = '(preise|prices|precios|prix|prezzi|cennik|precos|priser)';
  const DOMAIN_REGEX = '(com|cn|io)';
  const PRICES_URL_REGEX =
    // eslint-disable-next-line regexp/no-unused-capturing-group
    new RegExp(`https://${SUBDOMAIN_REGEX}.babbel.${DOMAIN_REGEX}/${PRICES_DOMAIN_REGEX}`);

  const returnToFromQuery = getReturnToUrl(queryParams, pricesURL(learnLang));
  const isPriceUrl = PRICES_URL_REGEX.test(returnToFromQuery);

  if (isPriceUrl) return pricesURL(learnLang);

  return LEARN_LANG_ALPHA3_REGEX.test(returnToFromQuery)
    ? returnToFromQuery.replace(LEARN_LANG_ALPHA3_REGEX, `learn_language_alpha3=${learnLang}`)
    : returnToFromQuery;
};
